import { useMutation } from '@apollo/client';

import type { DeleteSearchAlertMutation } from '@xing-com/crate-jobs-graphql';
import { DeleteSearchAlertDocument } from '@xing-com/crate-jobs-graphql';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useViewerData } from '@xing-com/hub';

import { deleteAlertFromCache } from './apollo-helpers';
import type { UseDeleteSearchAlert, SearchAlertHookParams } from './types';

const deleteOptimisticUpdate: DeleteSearchAlertMutation = {
  __typename: 'Mutation',
  deleteSearchAlert: {
    __typename: 'Empty',
  },
};

const DEFAULT_PARAMS = {
  disableOptimisticUpdate: false,
  onError: (): void => {},
};

export const useDeleteSearchAlert = (
  params?: SearchAlertHookParams
): UseDeleteSearchAlert => {
  const { disableOptimisticUpdate, onError } = {
    ...DEFAULT_PARAMS,
    ...(params ?? {}),
  };

  const [deleteSearchAlertX1] = useMutation(DeleteSearchAlertDocument);
  const userId = useViewerData().data?.user?.id;
  const trackNwt = useJobsNwtTracking();

  return ({ alertId, nwtTrackingOptions, onCompleted, showError = true }) => {
    deleteSearchAlertX1({
      variables: { id: alertId },
      update: deleteAlertFromCache(alertId, userId),
      optimisticResponse: disableOptimisticUpdate
        ? undefined
        : deleteOptimisticUpdate,
      onCompleted: (response) => {
        if (
          showError &&
          response.deleteSearchAlert?.__typename === 'SearchAlertErrorResponse'
        ) {
          onError();
        } else {
          if (nwtTrackingOptions) {
            trackNwt({
              event: 'deactivated',
              eventSchema: 'basic',
              page: 'jobs_search/serp',
              element: 'search_alert',
              itemUrn: `surn:x-xing:job_search_subscriptions:alert:${alertId}`,
              ...nwtTrackingOptions,
            });
          }
        }

        onCompleted?.(response);
      },
      onError: () => {
        if (showError) {
          onError();
        }
      },
    });
  };
};
